import React, { useMemo, useState } from "react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown, Coins } from "lucide-react";

/**
 * Step 3 — Commission (Care Team only).
 *
 * Props
 *   value         Object  { "svc:<service_id>": {value, type} }
 *   onChange(v)   Setter  — commits the whole map back to the wizard.
 *   categories    Array   Brand catalog categories (ordered as shown in Services).
 *   services      Array   Brand catalog services (unfiltered — the picture lists
 *                         every category + child regardless of staff training).
 *   currencyLabel String  e.g. "IDR" or "BHD". Shown next to the "Amount" option.
 *
 * Data-model contract (Iter-378):
 *   Commission is stored STRICTLY per treatment in `staff_commission`.
 *   Category-level entries are NEVER stored. The category row renders a
 *   dynamic SUMMARY LABEL calculated from the treatments beneath it and
 *   also offers a BULK-SET SHORTCUT that writes a value/type across every
 *   child service in one click. The shortcut inputs are UI-only state —
 *   they never land in `value`.
 */
export default function StaffCommissionStep({ value, onChange, categories, services, specializedServiceIds, currencyLabel }) {
  const [open, setOpen] = useState(() => new Set());
  // Per-category staging buffer for the bulk-set shortcut. Shape:
  //   { "<cat_id>": { value: "", type: "percent" } }
  const [bulkDraft, setBulkDraft] = useState({});

  // Restrict the catalog to the Care-Team member's Step-2 picks.
  const allowed = useMemo(
    () => new Set((specializedServiceIds || []).map(String)),
    [specializedServiceIds],
  );

  const svcByCat = useMemo(() => {
    const m = new Map();
    for (const s of services || []) {
      if (!allowed.has(String(s.id))) continue;
      const cid = s.category_id || "__uncategorized__";
      if (!m.has(cid)) m.set(cid, []);
      m.get(cid).push(s);
    }
    for (const list of m.values()) {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return m;
  }, [services, allowed]);

  const orderedCats = useMemo(() => {
    const withSvcs = (categories || []).filter((c) => (svcByCat.get(c.id) || []).length > 0);
    withSvcs.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    return withSvcs;
  }, [categories, svcByCat]);

  const amountLabel = currencyLabel || "IDR";

  const keyCat = (cid) => `cat:${cid}`;
  const keySvc = (sid) => `svc:${sid}`;

  const getSvcRow = (sid) => value?.[keySvc(sid)] || { value: 0, type: "percent" };

  const setSvcRow = (sid, patch) => {
    const next = { ...(value || {}) };
    const current = next[keySvc(sid)] || { value: 0, type: "percent" };
    const merged = { ...current, ...patch };
    merged.value = Number.isFinite(parseFloat(merged.value)) ? parseFloat(merged.value) : 0;
    next[keySvc(sid)] = merged;
    onChange(next);
  };

  const toggle = (cid) => {
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(cid)) n.delete(cid); else n.add(cid);
      return n;
    });
  };

  /**
   * Format a stored row as a short human label — "2%" or "Rp 50.000".
   * Thousands separator mirrors the convention already used across the
   * app ("." as the group separator for IDR / BHD-like locales).
   */
  const formatRowLabel = (row) => {
    if (row.type === "percent") return `${Number(row.value)}%`;
    const n = Number(row.value) || 0;
    return `${amountLabel} ${n.toLocaleString("de-DE")}`;
  };

  /**
   * Compute a read-only summary for a category based on the actual
   * stored service rows beneath it. The summary is never persisted.
   *
   *   - All children at zero / unset → "Not Set"
   *   - All children identical (value + type) → that value/type label
   *   - Any divergence → "Mixed"
   */
  const summarise = (list) => {
    if (!list || list.length === 0) return { label: "Not Set", tone: "muted" };
    const rows = list.map((s) => getSvcRow(s.id));
    const anyNonZero = rows.some((r) => Number(r.value) > 0);
    if (!anyNonZero) return { label: "Not Set", tone: "muted" };
    const first = rows[0];
    const allSame = rows.every(
      (r) => Number(r.value) === Number(first.value) && (r.type || "percent") === (first.type || "percent"),
    );
    if (!allSame) return { label: "Mixed", tone: "warn" };
    return { label: formatRowLabel(first), tone: "ok" };
  };

  const getDraft = (cid) => bulkDraft[cid] || { value: "", type: "percent" };

  const setDraft = (cid, patch) => {
    setBulkDraft((prev) => ({ ...prev, [cid]: { ...getDraft(cid), ...patch } }));
  };

  /**
   * Apply the staged (value, type) to every child service in the
   * category. Overwrites existing per-service rows unconditionally so
   * the summary snaps to a single uniform value immediately. Operators
   * can still fine-tune individual rows afterwards.
   */
  const applyBulk = (cid, list) => {
    const draft = getDraft(cid);
    const num = parseFloat(draft.value);
    if (!Number.isFinite(num)) return;
    const next = { ...(value || {}) };
    // Scrub any legacy category entry — Iter-378 never stores at the
    // category level, so an old `cat:*` key from a prior save becomes
    // dead weight the moment a bulk-set lands.
    delete next[keyCat(cid)];
    for (const svc of list) {
      next[keySvc(svc.id)] = { value: num, type: draft.type || "percent" };
    }
    onChange(next);
    // Clear the staging value but keep the type sticky — most ops
    // bulk-set multiple categories at the same % and this saves a
    // click per category.
    setBulkDraft((prev) => ({ ...prev, [cid]: { value: "", type: draft.type || "percent" } }));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Coins className="w-3.5 h-3.5" /> Commission rules
      </div>
      <p className="text-xs text-muted-foreground">
        Use the shortcut on a category row to set every treatment underneath in one click.
        Individual rows can still be fine-tuned after. The category label on the left shows
        the current state at a glance — a value, <em>Mixed</em>, or <em>Not Set</em>.
      </p>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table data-testid="staff-commission-table">
          <TableHeader>
            <TableRow className="bg-secondary/40">
              <TableHead className="w-1/2">Treatment</TableHead>
              <TableHead className="w-[200px]">Value</TableHead>
              <TableHead className="w-[200px]">Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orderedCats.map((cat) => {
              const list = svcByCat.get(cat.id) || [];
              const expanded = open.has(cat.id);
              const draft = getDraft(cat.id);
              const summary = summarise(list);
              const toneClass =
                summary.tone === "warn"
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : summary.tone === "ok"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-muted text-muted-foreground border-border";
              return (
                <React.Fragment key={cat.id}>
                  <TableRow
                    className="bg-muted/30 italic hover:bg-muted/50"
                    data-testid={`comm-cat-row-${cat.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => toggle(cat.id)}
                          className="flex items-center gap-2 text-left text-xs uppercase tracking-wider font-semibold text-foreground not-italic"
                          data-testid={`comm-cat-toggle-${cat.id}`}
                        >
                          {expanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                          <span>{cat.name}</span>
                          <span className="text-[10px] text-muted-foreground font-normal normal-case">
                            · {list.length} service{list.length === 1 ? "" : "s"}
                          </span>
                        </button>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full border not-italic font-medium ${toneClass}`}
                          data-testid={`comm-cat-summary-${cat.id}`}
                        >
                          {summary.label}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        value={draft.value}
                        onChange={(e) => setDraft(cat.id, { value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            applyBulk(cat.id, list);
                          }
                        }}
                        placeholder="Set all treatments..."
                        className="h-8 italic placeholder:italic placeholder:text-muted-foreground/70"
                        data-testid={`comm-cat-bulk-value-${cat.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select
                          value={draft.type || "percent"}
                          onValueChange={(v) => setDraft(cat.id, { type: v })}
                        >
                          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`comm-cat-bulk-type-${cat.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">%</SelectItem>
                            <SelectItem value="amount">{amountLabel}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-8 px-3 text-xs not-italic"
                          disabled={draft.value === "" || draft.value === null || !Number.isFinite(parseFloat(draft.value))}
                          onClick={() => applyBulk(cat.id, list)}
                          data-testid={`comm-cat-bulk-apply-${cat.id}`}
                        >
                          Apply
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {expanded && list.map((svc) => {
                    const row = getSvcRow(svc.id);
                    return (
                      <TableRow key={svc.id} className="bg-background">
                        <TableCell className="pl-10 text-sm">{svc.name}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            value={row.value || 0}
                            onChange={(e) => setSvcRow(svc.id, { value: e.target.value, type: row.type || "percent" })}
                            className="h-8"
                            data-testid={`comm-svc-value-${svc.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={row.type || "percent"}
                            onValueChange={(v) => setSvcRow(svc.id, { type: v, value: row.value || 0 })}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`comm-svc-type-${svc.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percent">%</SelectItem>
                              <SelectItem value="amount">{amountLabel}</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {orderedCats.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6" data-testid="commission-empty-state">
                  No services selected on Step 2 yet. Tick at least one service under Specialization to configure commissions.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Serialisation helpers — convert between the flat map used by the
 * UI (<cat|svc>:<id> → {value,type}) and the REST payload consumed
 * by the backend (`[{scope, scope_id, value, type}, …]`). Rows with
 * value <= 0 are dropped on serialize because the backend treats
 * missing rows as "no rule / zero commission".
 *
 * Iter-378 note: the UI no longer emits `cat:*` entries, but we still
 * accept them on hydrate (and ignore them on serialize) so a staff
 * record saved under the old schema loads cleanly without data loss.
 * ------------------------------------------------------------------ */
export function hydrateCommissionMap(rules) {
  const out = {};
  for (const r of rules || []) {
    const key = r.scope === "category" ? `cat:${r.scope_id}` : `svc:${r.scope_id}`;
    out[key] = { value: Number(r.value) || 0, type: r.type || "percent" };
  }
  return out;
}

export function serializeCommissionMap(map) {
  const rules = [];
  for (const [key, row] of Object.entries(map || {})) {
    if (!row) continue;
    const value = Number(row.value) || 0;
    if (value <= 0) continue;
    const [scopeTag, id] = key.split(":");
    rules.push({
      scope: scopeTag === "cat" ? "category" : "service",
      scope_id: id,
      value,
      type: row.type || "percent",
    });
  }
  return rules;
}
